'use client';

import { FC } from 'react';
import {
  Form as WebCompositionsForm,
  FormProps as WebCompositionsFormProps,
} from '@tuic/web-compositions/shared/Form';
import type PrismAPI from '@modules/prism-client/types/prism-types';
import { telemetryToFormAttributes } from '@modules/prism-client/lib/telemetry';
import { Html } from './Html';
import { FormField } from '@modules/prism-client/components/elements/FormField';
import { apiButtonToButtonProps, Button } from './Button';
import { apiTextButtonToTextButtonProps } from '@modules/prism-client/components/elements/TextButton';
import { documentToReactTree } from '@tuic/contentful';

export type FormProps = Omit<
  WebCompositionsFormProps,
  'submitButton' | 'cancelButton'
> & {
  data: PrismAPI.Form;
  fieldErrors?: Record<string, string>;
};

export const Form: FC<FormProps> = ({ data, fieldErrors, ...props }) => {
  const subline = data.header?.subline
    ? documentToReactTree(data.header.subline)
    : undefined;

  const header = {
    headline: data.header?.headline ?? props.header?.headline,
    subline: subline ?? props.header?.subline,
  };

  return (
    <WebCompositionsForm
      noValidate
      {...telemetryToFormAttributes(data.telemetry)}
      header={header}
      submitButton={apiButtonToButtonProps({ data: data.submitButton })}
      cancelButton={
        data.cancelButton
          ? apiTextButtonToTextButtonProps({ data: data.cancelButton })
          : undefined
      }
      {...props}
    >
      {props.children ?? getChildren(data.fields, fieldErrors)}
    </WebCompositionsForm>
  );
};

const getChildren = (
  fields: PrismAPI.FormField[],
  fieldErrors?: Record<string, string>,
): WebCompositionsFormProps['children'] => {
  return state => {
    return fields.map((field, idx) => {
      const disabled = state.pending || state.disabled;

      return (
        <FormField
          key={idx}
          data={field}
          disabled={disabled ? disabled : field.disabled}
          error={fieldErrors?.[field.name]}
        />
      );
    });
  };
};
